import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Trophy, Home, Settings, Plus, LogOut, Menu,
  ClipboardList, Building2, Database, ChevronDown, ChevronRight,
  BarChart3, ChevronLeft, Search, Shield, FileText,
} from 'lucide-react';
import ChessDirectorLogo from './ChessDirectorLogo';
import { useAuth } from '../contexts/AuthContext';
import { useOrganization } from '../contexts/OrganizationContext';
import { useTenant } from '../contexts/TenantContext';
import { useToast } from '../components/Toast';
import LanguageFlagPicker from './LanguageFlagPicker';

interface NavItem {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  href: string;
  badge?: number;
  children?: NavItem[];
}

interface AppShellProps {
  children: React.ReactNode;
}

const AppShell: React.FC<AppShellProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const { user, logout } = useAuth();
  // Access organization state
  const { state: orgState, setCurrentOrganization, createOrganization } = useOrganization();
  const { tenant } = useTenant();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Default sidebar to collapsed on tournament detail pages
  const isTournamentPage = /^\/tournaments\/[^/]+/.test(location.pathname);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(isTournamentPage);
  const [expandedItems, setExpandedItems] = useState<string[]>(['tournaments']);
  const [isMobile, setIsMobile] = useState(false);

  // Dropdown states
  const [orgMenuOpen, setOrgMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const orgMenuRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');

  // Create Org Modal from AppShell
  const [showCreateOrgModal, setShowCreateOrgModal] = useState(false);
  const [createOrgLoading, setCreateOrgLoading] = useState(false);
  const [orgMode, setOrgMode] = useState<'create' | 'join'>('create');
  const [joinCode, setJoinCode] = useState('');
  const [joinLoading, setJoinLoading] = useState(false);
  const [orgFormData, setOrgFormData] = useState({
    name: '',
    description: '',
    website: '',
    contactEmail: ''
  });

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Close sidebar when route changes on mobile
  useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false);
    }
  }, [location.pathname, isMobile]);

  // Auto-collapse sidebar on tournament detail pages
  useEffect(() => {
    if (isTournamentPage) {
      setSidebarCollapsed(true);
    }
  }, [isTournamentPage]);

  // Click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (orgMenuRef.current && !orgMenuRef.current.contains(event.target as Node)) {
        setOrgMenuOpen(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Get current organization ID for analytics link
  const currentOrgId = orgState.currentOrganization?.id;

  const navItems: NavItem[] = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: Home,
      href: '/dashboard',
    },
    {
      id: 'tournaments',
      label: 'Tournaments',
      icon: Trophy,
      href: '/tournaments',
    },
    {
      // Sign-up sheets with no games attached. Their own section rather than
      // a corner of Tournaments: a form is not an event anyone plays.
      id: 'forms',
      label: 'Forms',
      icon: FileText,
      href: '/manage/forms',
    },
    {
      id: 'scoring',
      label: 'Quick Scoring',
      icon: ClipboardList,
      href: '/tournaments/scoring',
    },
    {
      id: 'game-database',
      label: 'Game Database',
      icon: Database,
      href: '/game-database',
    },
    ...(currentOrgId ? [{
      id: 'analytics',
      label: 'Analytics',
      icon: BarChart3,
      href: `/organizations/${currentOrgId}/analytics`,
    }] : []),
  ];

  // Add Admin link if user is administrator
  if (user?.role === 'admin' || user?.email === 'aarushchugh1@gmail.com') {
    navItems.push({
      id: 'admin',
      label: 'Admin',
      icon: Shield,
      href: '/manage',
    });
  }

  const bottomNavItems: NavItem[] = [
    {
      id: 'settings',
      label: 'Settings',
      icon: Settings,
      href: '/settings',
    },
  ];

  const isActive = (href: string) => {
    if (href === '/dashboard') {
      return location.pathname === '/dashboard';
    }
    return location.pathname.startsWith(href);
  };

  const toggleExpanded = (id: string) => {
    setExpandedItems(prev =>
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      // Basic search redirect - assumes specialized search page isn't ready or we reuse tournament list
      // For now, let's redirect to a tournament search or list if available
      navigate(`/tournaments?search=${encodeURIComponent(searchQuery)}`);
    }
  };

  const handleCreateOrg = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!orgFormData.name.trim()) return;

    setCreateOrgLoading(true);
    try {
      const slug = orgFormData.name
        .toLowerCase()
        .replace(/[^a-z0-9_-]/g, '-')
        .replace(/--+/g, '-')
        .replace(/^-+|-+$/g, '');

      if (!slug) {
        toast.error('Invalid Name', 'Please enter a valid organization name');
        setCreateOrgLoading(false);
        return;
      }

      await createOrganization({
        ...orgFormData,
        slug
      });

      toast.success('Organization Created', 'Your new organization is ready');
      setShowCreateOrgModal(false);
      setOrgFormData({ name: '', description: '', website: '', contactEmail: '' });
      // Close dropdown if open
      setOrgMenuOpen(false);
    } catch (error: any) {
      console.error('Failed to create organization:', error);
      toast.error('Creation Failed', error?.response?.data?.error || 'Failed to create organization');
    } finally {
      setCreateOrgLoading(false);
    }
  };

  const handleJoinOrg = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!joinCode.trim()) return;

    setJoinLoading(true);
    try {
      const response = await fetch('/api/organizations/join', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ code: joinCode })
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Joined Organization', data.message);
        setShowCreateOrgModal(false);
        setJoinCode('');
        setOrgMenuOpen(false);

        // Refresh organizations
        window.location.reload();
      } else {
        toast.error('Join Failed', data.error || 'Failed to join organization');
      }
    } catch (error) {
      console.error('Failed to join organization:', error);
      toast.error('Join Failed', 'An unexpected error occurred');
    } finally {
      setJoinLoading(false);
    }
  };


  const currentOrg = orgState.currentOrganization || (orgState.organizations.length > 0 ? orgState.organizations[0] : null);

  const renderNavItem = (item: NavItem, depth = 0) => {
    const active = isActive(item.href);
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedItems.includes(item.id);
    const Icon = item.icon;

    if (sidebarCollapsed) {
      return (
        <div key={item.id} className="relative mb-1">
          {hasChildren ? (
            <button
              onClick={() => toggleExpanded(item.id)}
              className={`group w-full flex items-center justify-center p-2.5 rounded-xl transition-colors ${active
                ? 'text-gray-900 bg-gray-100'
                : 'text-gray-400 hover:text-gray-900 hover:bg-gray-50'
                }`}
              title={item.label}
            >
              <Icon className="h-5 w-5 transition-colors" />
            </button>
          ) : (
            <Link
              to={item.href}
              className={`group relative flex items-center justify-center p-2.5 rounded-xl transition-colors ${active
                ? 'text-gray-900 bg-gray-100'
                : 'text-gray-400 hover:text-gray-900 hover:bg-gray-50'
                }`}
              title={item.label}
            >
              <Icon className="h-5 w-5 transition-colors" />
              {item.badge && (
                <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-gray-900 rounded-full ring-2 ring-white"></span>
              )}
            </Link>
          )}
        </div>
      );
    }

    return (
      <div key={item.id} className="mb-0.5 relative">
        {hasChildren ? (
          <button
            onClick={() => toggleExpanded(item.id)}
            className={`group w-full flex items-center justify-between px-3 py-2 text-sm rounded-lg transition-colors ${active
              ? 'text-gray-900 bg-gray-100 font-semibold'
              : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50 font-medium'
              }`}
          >
            <div className="flex items-center gap-3">
              <Icon className={`h-[18px] w-[18px] transition-colors ${active ? 'text-gray-900' : 'text-gray-400 group-hover:text-gray-900'}`} />
              <span>{item.label}</span>
            </div>
            {isExpanded ? (
              <ChevronDown className={`h-4 w-4 transition-colors ${active ? 'text-gray-900' : 'text-gray-400'}`} />
            ) : (
              <ChevronRight className={`h-4 w-4 transition-colors ${active ? 'text-gray-900' : 'text-gray-400'}`} />
            )}
          </button>
        ) : (
          <Link
            to={item.href}
            className={`group flex items-center gap-3 px-3 py-2 text-sm rounded-lg transition-colors relative ${depth > 0 ? 'ml-4' : ''
              } ${active
                ? 'text-gray-900 bg-gray-100 font-semibold'
                : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50 font-medium'
              }`}
          >
            <Icon className={`h-[18px] w-[18px] transition-colors ${active ? 'text-gray-900' : 'text-gray-400 group-hover:text-gray-900'}`} />
            <span>{item.label}</span>
            {item.badge && (
              <span className="ml-auto bg-gray-900 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                {item.badge}
              </span>
            )}
          </Link>
        )}
        {hasChildren && isExpanded && !sidebarCollapsed && (
          <div className="mt-0.5 ml-0 space-y-0.5">
            {item.children!.map(child => renderNavItem(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-white/80 backdrop-blur border-b border-gray-100">
        <div className="flex items-center justify-between h-14 px-4">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <Menu className="h-6 w-6" />
          </button>
          <Link to="/dashboard" className="flex items-center">
            <ChessDirectorLogo size="sm" showText={false} />
          </Link>
          <div className="flex items-center gap-2">
            <Link
              to="/profile"
              className="relative w-8 h-8 rounded-full overflow-hidden ring-2 ring-gray-100"
            >
              {user?.avatarUrl ? (
                <img src={user.avatarUrl} alt="Profile" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-gray-900 text-white flex items-center justify-center text-xs font-medium">
                  {user?.firstName?.[0] || user?.username?.[0] || 'U'}
                </div>
              )}
            </Link>
          </div>
        </div>
      </header>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/20 backdrop-blur-sm z-50"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar — z-30 on desktop so modals (z-50) layer above it */}
      <aside
        className={`fixed top-0 left-0 h-full bg-white border-r border-gray-100 z-50 lg:z-30 transform transition-all duration-300 ease-[bezier(0.25,0.1,0.25,1)] ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          } lg:translate-x-0 ${sidebarCollapsed ? 'w-20' : 'w-64'
          }`}
      >
        <div className="flex flex-col h-full">
          {/* Sidebar Header */}
          <div className="flex items-center justify-center h-14 px-4 border-b border-gray-100">
            <Link to="/dashboard" className="flex items-center justify-center hover:opacity-80 transition-opacity">
              {!sidebarCollapsed ? (
                <span className="flex items-center gap-2.5">
                  <ChessDirectorLogo size="sm" showText={false} />
                  {!tenant && <span className="font-bold text-gray-900 text-sm">ChessDirector</span>}
                </span>
              ) : (
                <ChessDirectorLogo size="sm" showText={false} />
              )}
            </Link>
          </div>

          {/* Quick Create (Secondary/Sidebar version) */}
          <div className={`mb-4 mt-4 ${sidebarCollapsed ? 'px-3' : 'px-4'}`}>
            <Link
              to="/tournaments/new"
              className={`group flex items-center justify-center gap-2 w-full border border-gray-200 text-gray-500 hover:text-gray-900 hover:border-gray-300 bg-white hover:bg-gray-50 transition-colors ${sidebarCollapsed ? 'p-2.5 rounded-full' : 'px-4 py-2 rounded-full text-sm font-semibold'
                }`}
              title="New Tournament"
            >
              <Plus className="h-4 w-4 transition-transform group-hover:rotate-90" />
              {!sidebarCollapsed && <span>New Tournament</span>}
            </Link>
          </div>

          {/* Main Navigation */}
          <nav className={`flex-1 space-y-1 overflow-y-auto custom-scrollbar ${sidebarCollapsed ? 'px-3' : 'px-4'}`}>
            {navItems.map(item => renderNavItem(item))}
          </nav>

          {/* Bottom Navigation & Collapse */}
          <div className={`py-4 space-y-1 ${sidebarCollapsed ? 'px-3' : 'px-4'}`}>
            {bottomNavItems.map(item => renderNavItem(item))}

            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className={`w-full flex items-center gap-3 px-3 py-2 text-sm font-medium text-gray-400 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-colors mt-2 ${sidebarCollapsed ? 'justify-center' : ''}`}
              title={sidebarCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
            >
              {sidebarCollapsed ? (
                <ChevronRight className="h-5 w-5" />
              ) : (
                <>
                  <ChevronLeft className="h-[18px] w-[18px]" />
                  <span>Collapse Sidebar</span>
                </>
              )}
            </button>
          </div>

        </div>
      </aside>

      {/* Main Content */}
      <div className={`transition-all duration-300 ease-[bezier(0.25,0.1,0.25,1)] ${sidebarCollapsed ? 'lg:pl-20' : 'lg:pl-64'}`}>
        {/* Desktop Header / Top Bar — z-40 keeps it below modals (z-50); no backdrop-blur to avoid stacking context that traps child dropdowns */}
        <header className="hidden lg:flex items-center justify-between h-14 px-6 bg-white border-b border-gray-100 sticky top-0 z-40">

          {/* Left: Organization Switcher (hidden on tenant sites) */}
          {!tenant && (
            <div className="flex items-center gap-4 min-w-[200px]" ref={orgMenuRef}>
              <div className="relative">
                <button
                  onClick={() => setOrgMenuOpen(!orgMenuOpen)}
                  className="flex items-center gap-2.5 px-2.5 py-1.5 rounded-full hover:bg-gray-100 transition-colors max-w-xs"
                >
                  <div className="w-7 h-7 rounded-lg bg-gray-50 flex items-center justify-center text-gray-900 shrink-0 border border-gray-100 overflow-hidden">
                    {currentOrg?.logoUrl ? (
                      <img src={currentOrg.logoUrl} alt={currentOrg.name} className="w-full h-full object-cover" />
                    ) : (
                      <Building2 className="h-4 w-4 text-gray-400" />
                    )}
                  </div>
                  <div className="text-left">
                    <div className="text-sm font-bold text-gray-900 leading-none flex items-center gap-1.5">
                      {currentOrg?.name || 'Select Org'}
                      <ChevronDown className="h-3 w-3 text-gray-400" />
                    </div>
                    <div className="text-[10px] font-bold text-gray-400 mt-0.5 leading-none uppercase tracking-widest">
                      Organization
                    </div>
                  </div>
                </button>

                {/* Org Dropdown */}
                {orgMenuOpen && (
                  <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-xl shadow-xl border border-gray-100 py-1 z-[10000] animate-in fade-in zoom-in-95 duration-100">
                    <div className="px-3 py-2 border-b border-gray-50">
                      <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Switch Organization</span>
                    </div>
                    <div className="max-h-64 overflow-y-auto">
                      {orgState.organizations.map(org => (
                        <button
                          key={org.id}
                          onClick={() => {
                            setCurrentOrganization(org);
                            setOrgMenuOpen(false);
                            // Force refresh or navigation if needed, but context update should trigger re-renders
                            navigate('/dashboard');
                          }}
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900 text-left"
                        >
                          <div className={`w-2 h-2 rounded-full ${currentOrg?.id === org.id ? 'bg-gray-900' : 'bg-gray-200'}`} />
                          <span className="truncate">{org.name}</span>
                          {currentOrg?.id === org.id && <span className="ml-auto text-xs text-gray-900 font-medium">Active</span>}
                        </button>
                      ))}
                    </div>
                    <div className="border-t border-gray-50 mt-1 p-2">
                      <button
                        onClick={() => {
                          setShowCreateOrgModal(true);
                        }}
                        className="w-full flex items-center gap-2 px-3 py-2 text-xs font-medium text-gray-600 hover:text-gray-900 rounded-lg hover:bg-gray-50 text-left"
                      >
                        <Plus className="h-3 w-3" />
                        Create or Join Organization
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="h-4 w-px bg-gray-200 mx-2"></div>
            </div>
          )}

          {/* Tenant site: Show organization name (read-only) */}
          {tenant && currentOrg && (
            <div className="flex items-center gap-3 min-w-[200px]">
              <div className="w-7 h-7 rounded-lg bg-gray-50 flex items-center justify-center text-gray-900 shrink-0 border border-gray-100 overflow-hidden">
                {currentOrg.logoUrl ? (
                  <img src={currentOrg.logoUrl} alt={currentOrg.name} className="w-full h-full object-cover" />
                ) : (
                  <Building2 className="h-4 w-4 text-gray-400" />
                )}
              </div>
              <div className="text-left">
                <div className="text-sm font-bold text-gray-900 leading-none">
                  {currentOrg.name}
                </div>
                <div className="text-[10px] font-bold text-gray-400 mt-0.5 leading-none uppercase tracking-widest">
                  Organization
                </div>
              </div>
              <div className="h-4 w-px bg-gray-200 mx-2"></div>
            </div>
          )}

          {/* Center: Global Search */}
          <div className="flex-1 max-w-xl mx-auto px-4">
            <form onSubmit={handleSearch} className="relative group">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 group-focus-within:text-gray-600 transition-colors" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search tournaments..."
                className="w-full bg-gray-50 border border-gray-100 rounded-full py-1.5 pl-10 pr-4 text-sm text-gray-900 focus:bg-white focus:border-gray-300 focus:outline-none transition-colors placeholder-gray-400"
              />
            </form>
          </div>

          {/* Right: Language + Profile */}
          <div className="flex items-center gap-3">
            <LanguageFlagPicker compact />
            <div className="h-4 w-px bg-gray-200"></div>

            {/* User Profile Dropdown */}
            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center gap-1.5 p-1 rounded-full hover:bg-gray-100 transition-colors"
              >
                {user?.avatarUrl ? (
                  <img src={user.avatarUrl} alt="Profile" className="w-7 h-7 rounded-full object-cover" />
                ) : (
                  <div className="w-7 h-7 rounded-full bg-gray-900 text-white flex items-center justify-center text-xs font-bold">
                    {user?.firstName?.[0] || user?.username?.[0] || 'U'}
                  </div>
                )}
                <ChevronDown className="h-3 w-3 text-gray-400" />
              </button>

              {userMenuOpen && (
                <div className="absolute top-full right-0 mt-2 w-56 bg-white rounded-xl shadow-xl border border-gray-100 py-1 z-[10000] animate-in fade-in zoom-in-95 duration-100 origin-top-right">
                  <div className="px-4 py-3 border-b border-gray-50">
                    <p className="text-sm font-semibold text-gray-900 truncate">{user?.firstName} {user?.lastName}</p>
                    <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                  </div>
                  <div className="p-1">
                    <Link
                      to="/settings"
                      className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      <Settings className="h-4 w-4 text-gray-400" />
                      Settings
                    </Link>
                  </div>
                  <div className="border-t border-gray-50 p-1 mt-1">
                    <button
                      onClick={handleLogout}
                      className="flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg w-full text-left"
                    >
                      <LogOut className="h-4 w-4" />
                      Sign Out
                    </button>
                  </div>
                </div>
              )}
            </div>

          </div>
        </header>

        {/* Page Content */}
        <main className="pt-14 lg:pt-0 min-h-screen">
          {children}
        </main>
      </div>

      {/* Create/Join Organization Modal (Global) */}
      {showCreateOrgModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8 transform transition-all scale-100">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              {orgMode === 'create' ? 'Create Organization' : 'Join Organization'}
            </h2>

            {/* Toggle Switch */}
            <div className="flex p-1 bg-gray-100 rounded-lg mb-6">
              <button
                type="button"
                onClick={() => setOrgMode('create')}
                className={`flex-1 py-2 text-sm font-medium rounded-md transition-all ${orgMode === 'create'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-900'
                  }`}
              >
                Create New
              </button>
              <button
                type="button"
                onClick={() => setOrgMode('join')}
                className={`flex-1 py-2 text-sm font-medium rounded-md transition-all ${orgMode === 'join'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-900'
                  }`}
              >
                Join with Code
              </button>
            </div>

            {orgMode === 'create' ? (
              <form onSubmit={handleCreateOrg}>
                <div className="space-y-5">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Organization Name</label>
                    <input
                      type="text"
                      required
                      value={orgFormData.name}
                      onChange={e => setOrgFormData({ ...orgFormData, name: e.target.value })}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-gray-900 focus:bg-white outline-none transition-all"
                      placeholder="e.g. City Chess Club"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Description</label>
                    <textarea
                      value={orgFormData.description}
                      onChange={e => setOrgFormData({ ...orgFormData, description: e.target.value })}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-gray-900 focus:bg-white outline-none transition-all"
                      rows={3}
                      placeholder="Brief description..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Website</label>
                    <input
                      type="url"
                      value={orgFormData.website}
                      onChange={e => setOrgFormData({ ...orgFormData, website: e.target.value })}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-gray-900 focus:bg-white outline-none transition-all"
                      placeholder="https://"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Contact Email</label>
                    <input
                      type="email"
                      value={orgFormData.contactEmail}
                      onChange={e => setOrgFormData({ ...orgFormData, contactEmail: e.target.value })}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-gray-900 focus:bg-white outline-none transition-all"
                      placeholder="contact@example.com"
                    />
                  </div>
                </div>

                <div className="flex gap-4 mt-8">
                  <button
                    type="button"
                    onClick={() => setShowCreateOrgModal(false)}
                    className="flex-1 px-4 py-3 border border-gray-200 rounded-xl text-gray-700 font-semibold hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={createOrgLoading}
                    className="flex-1 px-4 py-3 bg-gray-900 text-white rounded-xl font-semibold hover:bg-black disabled:opacity-50 transition-colors shadow-lg"
                  >
                    {createOrgLoading ? 'Creating...' : 'Create Org'}
                  </button>
                </div>
              </form>
            ) : (
              <form onSubmit={handleJoinOrg}>
                <div className="space-y-5">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Join Code</label>
                    <input
                      type="text"
                      required
                      value={joinCode}
                      onChange={e => setJoinCode(e.target.value.toUpperCase())}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-gray-900 focus:bg-white outline-none transition-all uppercase tracking-widest font-mono text-center text-lg"
                      placeholder="XXXXXX"
                      maxLength={6}
                    />
                    <p className="mt-2 text-sm text-gray-500 text-center">
                      Enter the 6-character code provided by your organization admin.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4 mt-8">
                  <button
                    type="button"
                    onClick={() => setShowCreateOrgModal(false)}
                    className="flex-1 px-4 py-3 border border-gray-200 rounded-xl text-gray-700 font-semibold hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={joinLoading || joinCode.length < 6}
                    className="flex-1 px-4 py-3 bg-gray-900 text-white rounded-xl font-semibold hover:bg-black disabled:opacity-50 transition-colors shadow-lg"
                  >
                    {joinLoading ? 'Joining...' : 'Join Org'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AppShell;
